import { TFunction } from 'i18next';
import { RentalBookingDetails } from '../types/bookingCreation';
import { InvoiceConfig } from '../tenants/config';
import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';

export interface InvoiceValidationError {
    field: string;
    message: string;
}

/**
 * Validates that all data required for a legally compliant invoice is present.
 * The §14 UStG identity rules are German; the price is required everywhere.
 * The country parameter allows extending to other jurisdictions later.
 */
export const validateInvoiceData = (
    booking: RentalBookingDetails,
    invoiceConfig: InvoiceConfig,
    t: TFunction,
    country = 'DE',
    breakdown?: PriceBreakdown,
): InvoiceValidationError[] => {
    const errors: InvoiceValidationError[] = [];

    // #1203: the invoice bills what the backend computed — line items and
    // totals both. While that breakdown is loading or after it failed there is
    // no second way to price the booking, and inventing one is precisely the
    // divergence this change removes, so generation is blocked and the operator
    // is told why.
    if (!breakdown) {
        errors.push({
            field: 'price',
            message: t('invoice.missing_price'),
        });
    }

    if (country === 'DE') {
        validateDE(booking, invoiceConfig, t, errors);
    }

    return errors;
};

const isEmpty = (value: string | undefined | null): boolean => {
    return !value || value.trim().length === 0;
};

const isTodoPlaceholder = (value: string | undefined | null): boolean => {
    return !value || value.trim().toUpperCase().startsWith('TODO');
};

const validateDE = (
    booking: RentalBookingDetails,
    invoiceConfig: InvoiceConfig,
    t: TFunction,
    errors: InvoiceValidationError[],
): void => {
    // Invoice number (§14 Abs. 4 Nr. 4 UStG)
    if (isEmpty(booking.invoiceNumber)) {
        errors.push({
            field: 'invoiceNumber',
            message: t('invoice.missing_invoice_number'),
        });
    }

    // Supplier identity (§14 Abs. 4 Nr. 1 UStG)
    if (
        isEmpty(invoiceConfig.companyName)
        || isEmpty(invoiceConfig.companyAddress)
        || isEmpty(invoiceConfig.companyZip)
        || isEmpty(invoiceConfig.companyCity)
    ) {
        errors.push({
            field: 'supplierAddress',
            message: t('invoice.missing_supplier_address'),
        });
    }

    // Supplier VAT ID (§14 Abs. 4 Nr. 2 UStG)
    if (isEmpty(invoiceConfig.taxId)) {
        errors.push({
            field: 'taxId',
            message: t('invoice.missing_supplier_vat_id'),
        });
    }

    // Customer identity (§14 Abs. 4 Nr. 1 UStG)
    const customer = booking.customerDetails;
    if (isEmpty(customer?.firstName) || isEmpty(customer?.lastName)) {
        errors.push({
            field: 'customerName',
            message: t('invoice.missing_customer_name'),
        });
    }

    if (
        isEmpty(customer?.address)
        || isEmpty(customer?.zipCode)
        || isEmpty(customer?.city)
    ) {
        errors.push({
            field: 'customerAddress',
            message: t('invoice.missing_customer_address'),
        });
    }

    // Service dates (§14 Abs. 4 Nr. 6 UStG)
    if (!booking.checkIn || !booking.checkOut) {
        errors.push({
            field: 'dates',
            message: t('invoice.missing_dates'),
        });
    }

    // Bank details — not legally required but expected for payment
    if (isTodoPlaceholder(invoiceConfig.iban)
        || isTodoPlaceholder(invoiceConfig.bankName)) {
        errors.push({
            field: 'bankDetails',
            message: t('invoice.missing_bank_details'),
        });
    }
};
